'use client';

import { useTranslation } from '@/lib/i18n/context';

import { BentoGrid, type BentoCard } from './bento-grid';
import { CARDS_AR, SECTION_AR } from './platform-bento.ar';
import { CONNECTIONS } from './platform-connections';

const CARDS: BentoCard[] = [
  {
    id: 'omnichannel',
    title: 'One brain, every channel',
    description:
      'Voice, chat, email, WhatsApp, Slack, social. Your AI agent shows up consistent and contextual everywhere your customers reach you, no per-channel rebuilds.',
    image: 'https://platform.open.cx/changelog/phone-agent-workflows.jpg',
    layout: 'horizontal',
    modal: {
      eyebrow: 'Omnichannel by design',
      headline: 'One AI engine across every channel',
      lead: 'Most vendors sell you a chat bot, a separate voice bot, and yet another email responder, three products, three bills, three things to keep in sync. Open is one agent, one brain, everywhere your customers reach you.',
      blocks: [
        {
          type: 'text',
          body: [
            'Every disconnected bot carries its own configuration, its own knowledge base, and its own tone of voice. Customers feel the seams the instant they move from a chat widget to a phone call and have to start over. Internally, your team maintains the same answer in four places and watches them drift apart.',
            'Open collapses all of it into a single agent with one personality and one source of truth. Write an instruction once and it is live on every channel in seconds, no copy-paste, no per-channel QA, no drift.',
          ],
        },
        {
          type: 'video',
          src: '/home/channels.mp4',
          caption: 'The same agent, live across chat, voice, email, WhatsApp, and social.',
        },
        {
          type: 'stats',
          items: [
            { value: '8+', label: 'Channels, one agent' },
            { value: '1', label: 'Knowledge brain' },
            { value: '0', label: 'Per-channel rebuilds' },
          ],
        },
        {
          type: 'text',
          heading: 'Context that follows the customer',
          body: 'A customer can start in chat, escalate to a phone call, and finish over email without ever repeating themselves. The full conversation history, their account context, and every action taken travel with them across the handoff, to the AI and to any human who steps in.',
        },
        {
          type: 'channels',
          heading: 'Discover our channels',
          subheading:
            'Every channel runs on the same agent. Explore how each one works.',
          items: [
            { name: 'Voice', href: '/channels/voice' },
            { name: 'Chat', href: '/channels/chat' },
            { name: 'Email', href: '/channels/email' },
            { name: 'WhatsApp', href: '/channels/whatsapp' },
            { name: 'Slack', href: '/channels/slack' },
            { name: 'Social', href: '/channels/social' },
          ],
        },
        {
          type: 'faq',
          heading: 'Frequently asked',
          items: [
            {
              q: 'Do I have to set up each channel separately?',
              a: 'No. One agent, one knowledge base, and one set of guardrails serve every channel. You update an instruction once and it goes live everywhere in seconds.',
            },
            {
              q: 'Can a conversation move between channels?',
              a: 'Yes. A customer can start in chat, escalate to a phone call, and finish over email. The full history and context follow them across every handoff, to the AI and to any human who steps in.',
            },
            {
              q: 'Which channels are supported?',
              a: 'Voice, chat, email, SMS, WhatsApp, Instagram, Messenger, Slack, and Discord, with native telephony built in.',
            },
            {
              q: 'Do you work with my phone system?',
              a: 'Bring your own number, or connect Twilio, Vonage, or RingCentral. Adding a new channel takes days, not quarters.',
            },
          ],
        },
      ],
      cta: { label: 'See it in action', href: '/demo' },
    },
  },
  {
    id: 'workflows',
    title: 'Workflows that take action',
    description:
      "From refunds to account changes to escalations, the agent doesn't just chat, it calls your APIs and gets the job done.",
    image: 'https://platform.open.cx/changelog/companion-planning-mode.jpg',
    layout: 'vertical',
    modal: {
      eyebrow: 'Agentic workflows',
      headline: 'Real actions, not deflections',
      lead: "Half of your support volume isn't questions, it's requests to do something. A deflection to a help article doesn't resolve those. Open's agent connects to your systems and finishes the job inside the same conversation.",
      blocks: [
        {
          type: 'text',
          body: [
            'Process a refund in Stripe, update a shipping address in Shopify, reset a password, apply account credit, escalate to the on-call engineer in PagerDuty. Instead of telling the customer where to click, the agent calls your APIs and completes the task end-to-end, then confirms it back in plain language.',
            'You decide exactly how much autonomy it has. Low-risk actions run instantly; anything sensitive pauses for a one-click human approval in Slack, with the full context attached.',
          ],
        },
        {
          type: 'video',
          src: '/home/integrations.mp4',
          caption: 'Native connectors to Stripe, Shopify, Salesforce, and 100+ tools.',
        },
        {
          type: 'stats',
          items: [
            { value: '100+', label: 'Native integrations' },
            { value: '100%', label: 'Actions audit-logged' },
            { value: '<1 hr', label: 'To ship a workflow' },
          ],
        },
        {
          type: 'text',
          heading: 'Build it your way',
          body: 'Compose workflows in a visual editor your ops team can own, or drop into custom code when logic gets complex. Every run executes in a sandbox with a complete audit trail, what ran, on whose approval, and what changed, so you can hand the agent real power without losing control.',
        },
        {
          type: 'faq',
          heading: 'Frequently asked',
          items: [
            {
              q: 'Can the agent actually do things, or just answer?',
              a: 'It takes real actions. The agent calls your APIs to process refunds, update addresses, reset passwords, and escalate, completing the task inside the same conversation, then confirming it in plain language.',
            },
            {
              q: 'How do I keep risky actions safe?',
              a: 'Every action runs sandboxed with a full audit trail. High-risk steps pause for a one-click human approval in Slack, with the full context attached.',
            },
            {
              q: 'What can it connect to?',
              a: 'Stripe, Shopify, Salesforce, and 100+ tools natively, plus any internal API or database you expose.',
            },
            {
              q: 'Do I need engineers to build workflows?',
              a: 'No. Your ops team can compose workflows in a visual builder, and drop into custom code only when the logic gets complex.',
            },
          ],
        },
      ],
      cta: { label: 'Explore workflows', href: '/workflows' },
    },
  },
  {
    id: 'knowledge',
    title: 'Knowledge that stays fresh',
    description:
      'Auto-ingest your docs, learn from every resolved ticket, and roll back any change with one click.',
    image: 'https://platform.open.cx/changelog/ai-instructions-ui.jpg',
    layout: 'vertical',
    modal: {
      eyebrow: 'Continuous learning',
      headline: 'A knowledge base that maintains itself',
      lead: 'Your help center, internal wiki, and product changelog change every single day. An AI agent trained on a static export is wrong within a week. Open evolves with your product, automatically.',
      blocks: [
        {
          type: 'text',
          body: [
            'Open continuously syncs your sources, notices when an answer has gone stale, and flags the questions it cannot yet answer confidently. Instead of guessing what to document, you get a ranked list of exactly what customers are asking that your knowledge base is missing.',
            'The result is an agent that gets more accurate the more it is used, and a team that spends its time closing real gaps instead of auditing the bot.',
          ],
        },
        {
          type: 'video',
          src: '/home/data-sources.mp4',
          caption: 'Auto-sync from Notion, Confluence, Zendesk, Intercom, and live web crawls.',
        },
        {
          type: 'logos',
          variant: 'grid',
          heading: 'Connect any source',
          subheading:
            'Every knowledge base, helpdesk, and tool we support out of the box, plus anything Airbyte connects to, and any REST API.',
          items: CONNECTIONS,
        },
        {
          type: 'stats',
          items: [
            { value: '50+', label: 'Sources supported' },
            { value: '1-click', label: 'Rollback' },
            { value: '24/7', label: 'Freshness monitoring' },
          ],
        },
        {
          type: 'text',
          heading: 'Versioned, and reversible',
          body: 'Every instruction and every knowledge change is versioned. If an edit quietly tanks your resolution rate, roll back in one click and see exactly what changed, who changed it, and when. Nothing about your agent is a black box.',
        },
        {
          type: 'faq',
          heading: 'Frequently asked',
          items: [
            {
              q: 'How does the agent stay up to date?',
              a: 'It continuously syncs your sources, Notion, Confluence, Zendesk, Intercom, Google Docs, and live web crawls, and flags answers that have gone stale automatically.',
            },
            {
              q: 'What if a change makes things worse?',
              a: 'Every instruction is versioned. If an edit drops your resolution rate, roll back in one click and see exactly what changed, who changed it, and when.',
            },
            {
              q: "How do I know what's missing?",
              a: "Knowledge-gap detection ranks the questions customers ask that your knowledge base can't answer yet, so you fix real gaps instead of guessing.",
            },
            {
              q: 'Does it learn from my team?',
              a: "Yes. It learns from every human-resolved ticket, getting more accurate the more it's used.",
            },
          ],
        },
      ],
      cta: { label: 'See knowledge gaps', href: '/knowledge-gap-detection' },
    },
  },
  {
    id: 'helpdesk',
    title: 'Works with any helpdesk',
    description:
      "Zendesk, Intercom, Salesforce, HubSpot, Freshdesk, Twilio Flex, or Open's own. It sits on top of the stack you already run. No migration.",
    image: 'https://platform.open.cx/changelog/versioning-audit.jpg',
    layout: 'vertical',
    modal: {
      eyebrow: 'Native integrations',
      headline: 'Sits on top of the helpdesk you already run',
      lead: 'No rip-and-replace, no data migration, no retraining your team. Open plugs into your existing helpdesk, works your tickets alongside your agents, and syncs every change back, so nobody has to leave the tools they know.',
      blocks: [
        {
          type: 'text',
          body: [
            'Switching helpdesks is one of the most painful projects a support org can take on, months of migration, lost history, and a team relearning everything. Open skips all of it. It connects to your current platform through a deep, native integration and starts resolving tickets on day one.',
            'The AI picks up conversations in the same inbox your agents already use. When it hands off, the human sees the full thread. When a human resolves a ticket, the AI learns from it. Everyone works in one place.',
          ],
        },
        {
          type: 'video',
          src: '/home/ticketing-system.mp4',
          caption: 'Open working tickets natively inside your existing helpdesk.',
        },
        {
          type: 'stats',
          items: [
            { value: '7+', label: 'Helpdesks supported' },
            { value: '<1 hr', label: 'To connect' },
            { value: '0', label: 'Migration required' },
          ],
        },
        {
          type: 'logos',
          heading: 'Native on your helpdesk',
          subheading:
            'Deep, two-way integrations with the platforms you already run.',
          items: [
            { name: 'Zendesk', src: '/helpdicks/zendesk.png', href: '/helpdesks/zendesk' },
            { name: 'Intercom', src: '/helpdicks/intercom.png', href: '/helpdesks/intercom' },
            { name: 'Salesforce', src: '/helpdicks/salesforce.png', href: '/helpdesks/salesforce' },
            { name: 'HubSpot', src: '/helpdicks/hubspot.png', href: '/helpdesks/hubspot' },
            { name: 'Freshdesk', src: '/helpdicks/freshdesk.png', href: '/helpdesks/freshdesk' },
            { name: 'Twilio Flex', src: '/helpdicks/flex.png', href: '/helpdesks/twilio-flex' },
            { name: 'Front', src: '/helpdicks/front.png', href: '/helpdesks/open' },
            { name: 'Open Helpdesk', src: '/open-logo.svg', href: '/helpdesks/open' },
          ],
        },
        {
          type: 'text',
          heading: 'Deep, two-way sync',
          body: 'This is more than a webhook. Tickets, tags, macros, custom fields, assignees, internal notes, and CSAT flow in both directions in real time. Your reporting stays accurate, your automations keep firing, and your SLAs keep tracking. The AI simply becomes your fastest, always-on teammate inside the system of record you already trust.',
        },
        {
          type: 'faq',
          heading: 'Frequently asked',
          items: [
            {
              q: 'Do I have to migrate off my current helpdesk?',
              a: 'No. Open sits on top of the platform you already run and starts resolving tickets on day one, no migration, no lost history.',
            },
            {
              q: 'Which helpdesks do you support?',
              a: 'Zendesk, Intercom, Salesforce Service Cloud, HubSpot Service Hub, Freshdesk, Twilio Flex, and our own Open Helpdesk, plus custom platforms via REST API and webhooks.',
            },
            {
              q: 'Will it break my reporting and automations?',
              a: 'No. Tickets, tags, macros, custom fields, assignees, and CSAT sync both ways in real time, so your reporting stays accurate and your SLAs keep tracking.',
            },
            {
              q: 'How long does it take to connect?',
              a: 'Under an hour, through a deep native integration. Your team keeps working in the tools they already know.',
            },
          ],
        },
      ],
      cta: { label: 'See all helpdesks', href: '/helpdesks' },
    },
  },
];

export const PlatformBento = () => {
  const { locale } = useTranslation();
  const isAr = locale === 'ar';
  const section = isAr
    ? SECTION_AR
    : {
        title: 'Automate and unify support',
        subtitle:
          'A single AI engine, powerful, and shared across every channel. Consistent and relied on by the most mission-critical enterprises in the world.',
      };

  return (
    <BentoGrid
      title={section.title}
      subtitle={section.subtitle}
      cards={isAr ? CARDS_AR : CARDS}
      alwaysShowExpand
    />
  );
};

export default PlatformBento;
